export enum LayoutTypes {
  SCREEN_GROUP = "screenGroup",
  GROUP = "group",
  GRID = "grid",
  FORM = "form",
  FIELD_GROUP = "fieldGroup",
}

export enum ComponentType {
  BUTTON = "button",
  INFO_CARD = "infoCard",
  TEXT_FIELD = "textField",
  TEXT_AREA = "textArea",
  RADIO_FIELD = "radioField",
  DATE_FIELD = "dateField",
  DATETIME_FIELD = "datetimeField",
  SEARCHABLE_DROPDOWN = "searchableDropdown",
  SELECT_DROPDOWN = "selectDropdown",
  IMAGE_UPLOAD = "imageUpload",
  NAV_ITEM = "navItem",
  COLLAPSIBLE_NAV_ITEM = "collapsibleNavItem",
  SUMMARY_LIST = "summeryList",
  WEB_VIEW = "webView",
  SUB_MODULE = "subModule",
  MULTI_SUB_LIST = "multiSubList",
  MULTI_CHECKBOX = "multiCheckbox",
  REPORT = "report",
  FILES = "files",
  CHECKBOX = "checkbox",
  CUSTOM = "custom",
  INFO_CARDS = "infoCards",
  CHECK_IN_OUT_COMPONENT = "checkInOutComponent",
  LEAVE_BALANCE = "leaveBalance",
  WORKFLOW_STATE = "workflowState",
  PROFILE_HEADER_CARD = "profileHeaderCard",
  PROFILE_CONTACT_INFO = "profileContactInfo",
  PROFILE_DEPARTMENT_INFO = "profileDepartmentInfo",
  PROFILE_TABS = "profileTabs",
}

export enum FlexDirection {
  ROW = "row",
  COLUMN = "column",
}

export enum WidthMode {
  FULL = "full",
  WRAP = "wrap",
}

export enum HeightMode {
  FULL = "full",
  WRAP = "wrap",
}

export enum InputType {
  TEXT = "text",
  EMAIL = "email",
  PASSWORD = "password",
  NUMBER = "number",
  PHONE = "phone",
  URL = "url",
}

export enum ValidationRule {
  REQUIRED = "required",
  EMAIL = "email",
  MIN_LENGTH = "minLength",
  MAX_LENGTH = "maxLength",
  PATTERN = "pattern",
  MIN_VALUE = "minValue",
  MAX_VALUE = "maxValue",
  CUSTOM = "custom",
  /** Expression-based validation: Condition uses [field] refs resolved from row data. */
  CONDITION = "condition",
}

/** Validation that evaluates an expression per row (e.g. "[qty] >= [issued_qty]"). */
export interface ConditionalValidation {
  condition: string;
  message: string;
  isWarning?: boolean;
}

export enum ActionType {
  PDF_DOWNLOAD = "PDF_DOWNLOAD",
  VIEW_REPORT = "VIEW_REPORT",
  NAVIGATE = "Navigate",
  NAVIGATE_LOAD = "Navigate_Load",
  EDIT = "Edit",
  DELETE = "Delete",
}



export interface ValidationConfig {
  rule: ValidationRule;
  value?: string | number;
  message?: string;
  /** For rule === CONDITION: expression e.g. "[qty] >= [issued_qty]". Refs map to Json_Attribute or name. */
  condition?: string;
  isWarning?: boolean;
}

export interface ComponentStyle {
  backgroundColor?: string;
  padding?: number;
  margin?: number;
  marginTop?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
  paddingTop?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingRight?: number;
  gap?: number;
  direction?: FlexDirection;
  widthMode?: WidthMode; // full (default) | wrap
  heightMode?: HeightMode; // full (default) | wrap
  itemWidth?: WidthMode;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
  fontSize?: number;
  fontWeight?: string;
  textColor?: string;
  textAlign?: "left" | "center" | "right" | "justify";
  overflow?: string;
  overflowY?: string;
  overflowX?: string;
  height?: string | number;
  maxHeight?: string | number;
  display?: string;
  justifyContent?: string;
  alignItems?: string;
  // Add more style properties as needed
}

/** Condition for field visibility: show when the referenced field's selected option label matches. */
export interface FieldVisibilityWhen {
  field: string;
  label: string;
}

/** Screen-level rule: show given fields only when the condition matches (e.g. Bill mode = Cheque). Reusable across screens. */
export interface FieldVisibilityRule {
  when: FieldVisibilityWhen;
  showFields: string[];
}

/** Permission key that gates this component's visibility based on the screen's userlevelpermissions entry */
export type PermissionKey = "Add" | "Edit" | "Delete" | "Unlock" | "View";

export interface BaseComponent {
  componentType: LayoutTypes | ComponentType;
  style?: ComponentStyle;
  childComponents?: ComponentConfig[];
  // Legacy/backward compatibility properties
  Json_Attribute?: string;
  BindValue?: string;
  Label?: string;
  visibility?: number[];
  columnNumber?: string;
  HideOnStates?: string[];
  /** If set, this component is hidden when the user lacks this permission for the screen's screenId */
  permissionKey?: PermissionKey;
}

export interface DrawerSubmenuItem {
  title: string;
  screenId?: string;
  iconPath?: string;
}

export interface DrawerSubmenu {
  title: string;
  mode?: "inline" | "outside";
  backgroundColor?: string;
  contentTextColor?: string;
  items: DrawerSubmenuItem[];
}

export interface DrawerMenuItem {
  title: string;
  moduleId?: string; // Add this line
  screenId?: string;
  iconPath?: string;
  backgroundColor?: string;
  contentTextColor?: string;
  submenu?: DrawerSubmenu;
}

export interface DrawerMenuConfig {
  backgroundColor?: string;
  contentTextColor?: string;
  menu: DrawerMenuItem[];
}

export interface FabAction {
  type: "add" | "edit" | "delete" | "custom";
  screenId?: string;
  action?: string;
  tooltip?: string;
  permissionKey?: PermissionKey;
}

export interface ScreenGroupComponent extends BaseComponent {
  componentType: LayoutTypes.SCREEN_GROUP;
  fabAction?: FabAction;
  title?: string;
  drawerMenuEnabled?: boolean;
  drawerMenu?: DrawerMenuConfig;
  /** API name to call when the screen loads (e.g. "supplier_summary"). Calls GET /pabws/screen/{loadApiName}. Response is not used. */
  loadApiName?: string;
  /** Screen ID used to look up this screen's entry in userlevelpermissions */
  screenId?: string;
}

export interface GroupComponent extends BaseComponent {
  componentType: LayoutTypes.GROUP;
  Label?: string;
  BindValue?: string;
}

export interface ButtonComponent extends BaseComponent {
  componentType: ComponentType.BUTTON;
  name?: string;
  onPressAction?: string; // identifier for action routing
  disabled?: boolean;
  loading?: boolean;
  Label?: string;
  BindValue?: string;
  ActionType?: string;
  ScreenId?: string;
  Settings?: string; // For actions like PDF that need a settings name
  Enabled?: {
    Enable?: {
      Json_Attribute: string;
      values: string[];
    };
    Mode: string[];
  };
  LockBinding?: boolean;
  Navigate?: string;
  toolTip?: string;
}

export interface InfoCardComponent extends BaseComponent {
  componentType: ComponentType.INFO_CARD;
  name: string;
  value: string | number;
  valueFormat?: "number" | "currency" | "percent" | "string";
  icon?: string;
}

export interface GridComponent extends BaseComponent {
  componentType: LayoutTypes.GRID;
  columnNumber?: string;
  onPress?: (item: ComponentConfig) => void;
}

export interface FieldGroupComponent extends BaseComponent {
  componentType: LayoutTypes.FIELD_GROUP;
  columnNumber?: string;
  onPress?: (item: ComponentConfig) => void;
}

export interface FormField {
  IncludeLabel?: boolean;
  Label?: string;
  PlaceHolder?: string;
  IsMandatory?: boolean;
  BindValue?: string;
  Json_Attribute?: string;
  componentType?: ComponentType | LayoutTypes;
  showInSummary?: boolean;
  IsReadOnly?: boolean;
  AutoPopulate?: boolean;
  AutoPopulate_format?: string;
  IsVerticalView?: boolean;
  inputType?: InputType;
  LookupName?: string;
  BindScreen?: {
    EnableAdding?: boolean;
    BindScreen?: string;
  };
  BindParent?: Record<string, string>;
  InputValue?: string;
  TableName?: string;
  Name?: string;
  DefaultToday?: boolean;
  [key: string]: any; // Allow additional properties for flexibility
}

export interface FormComponent extends BaseComponent {
  componentType: LayoutTypes.FORM;
  Name?: string;
  TableName?: string;
  DisplayName?: string;
  columnNumber?: string;
  Workflow?: string; // Workflow name for workflow state controls
  workflow?: string; // Alternative lowercase property name
  onPress?: (item: ComponentConfig) => void;
  DisableSubmit?: boolean;
  LockOnSubmit?: boolean;
  SubmitActions?: {
    Action: string;
    Navigate?: string;
  }[];
  Fields?: FormField[]; // childComponents can have Fields property
  /** Show listed fields only when the given condition matches (e.g. when Bill mode is Cheque). Driven by screen settings, reusable. */
  fieldVisibilityRules?: FieldVisibilityRule[];
  formActions?: {
    name?: string;
    type?: string;
    icon?: string;
    onPressAction?: string;
    [key: string]: any;
  }[];
}

// Form Field Components
export interface TextFieldComponent extends BaseComponent {
  componentType: ComponentType.TEXT_FIELD;
  name: string;
  label?: string;
  placeholder?: string;
  inputType?: InputType;
  defaultValue?: string;
  validation?: ValidationConfig[];
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  helperText?: string;
  errorText?: string;
  Json_Attribute?: string;
  BindValue?: string;
  AutoPopulate?: boolean;
  AutoPopulate_format?: string;
  IsVerticalView?: boolean;
  EnableEditForControl?: boolean;
  // Allow any additional properties for flexibility
  [key: string]: any;
}

export interface TextAreaComponent extends BaseComponent {
  componentType: ComponentType.TEXT_AREA;
  name: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  validation?: ValidationConfig[];
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  rows?: number;
  helperText?: string;
  errorText?: string;
  Json_Attribute?: string;
  BindValue?: string;
}

export interface RadioOption {
  label: string;
  value: string | number | boolean;
  disabled?: boolean;
}

export interface RadioFieldComponent extends BaseComponent {
  componentType: ComponentType.RADIO_FIELD;
  name: string;
  label?: string;
  placeholder?: string;
  options: RadioOption[];
  defaultValue?: string | number | boolean;
  validation?: ValidationConfig[];
  required?: boolean;
  disabled?: boolean;
  direction?: FlexDirection;
  helperText?: string;
  errorText?: string;
}

export interface DateFieldComponent extends BaseComponent {
  componentType: ComponentType.DATE_FIELD;
  name: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  validation?: ValidationConfig[];
  required?: boolean;
  disabled?: boolean;
  isReadOnly?: boolean;
  minDate?: string;
  maxDate?: string;
  dateFormat?: string; // e.g., "MM/dd/yyyy", "dd-MM-yyyy"
  helperText?: string;
  errorText?: string;
  Json_Attribute?: string;
  BindValue?: string;
  IsVerticalView?: boolean;
  DefaultToday?: boolean;
}

export interface DateTimeFieldComponent extends BaseComponent {
  componentType: ComponentType.DATETIME_FIELD;
  name: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  validation?: ValidationConfig[];
  required?: boolean;
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
  dateFormat?: string; // e.g., "MM/dd/yyyy", "dd-MM-yyyy"
  timeFormat?: string; // e.g., "HH:mm", "hh:mm a"
  helperText?: string;
  errorText?: string;
  Json_Attribute?: string;
  BindValue?: string;
  IsVerticalView?: boolean;
  DefaultToday?: boolean;
  showTimePicker?: boolean; // Whether to show time picker (default: true)
}

export interface DropdownOption {
  label: string;
  value: string | number | boolean;
  disabled?: boolean;
  icon?: string;
}

export interface SearchableDropdownComponent extends BaseComponent {
  componentType: ComponentType.SEARCHABLE_DROPDOWN;
  name: string;
  label?: string;
  placeholder?: string;
  options?: DropdownOption[];
  defaultValue?: string | number | boolean;
  validation?: ValidationConfig[];
  required?: boolean;
  disabled?: boolean;
  isReadOnly?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  multiple?: boolean;
  maxSelections?: number;
  helperText?: string;
  errorText?: string;
  onSearchAction?: string; // for dynamic search
  lookupName?: string; // for lookup-based options
  parentField?: string; // for dependent dropdowns
  Json_Attribute?: string;
  BindValue?: string;
  IncludeNotSet?: boolean;
  IsVerticalView?: boolean;
  PopupScreenId?: string;
}

export interface SelectDropdownComponent extends BaseComponent {
  componentType: ComponentType.SELECT_DROPDOWN;
  name: string;
  label?: string;
  placeholder?: string;
  options?: DropdownOption[];
  defaultValue?: string | number | boolean;
  validation?: ValidationConfig[];
  required?: boolean;
  disabled?: boolean;
  isReadOnly?: boolean;
  multiple?: boolean;
  maxSelections?: number;
  helperText?: string;
  errorText?: string;
  lookupName?: string; // for lookup-based options
  parentField?: string; // for dependent dropdowns
  Json_Attribute?: string;
  BindValue?: string;
  IncludeNotSet?: boolean;
  IsVerticalView?: boolean;
  PopupScreenId?: string;
}

export interface ImageUploadComponent extends BaseComponent {
  componentType: ComponentType.IMAGE_UPLOAD;
  name: string;
  label?: string;
  placeholder?: string;
  validation?: ValidationConfig[];
  required?: boolean;
  disabled?: boolean;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  acceptedFileTypes?: string[]; // e.g., ["image/jpeg", "image/png"]
  uploadAction?: string; // identifier for upload action routing
  previewEnabled?: boolean;
  helperText?: string;
  errorText?: string;
}

export interface NavItemComponent extends BaseComponent {
  componentType: ComponentType.NAV_ITEM;
  name: string;
  label: string;
  icon?: string;
  route?: string;
  tooltip?: string;
  isActive?: boolean;
  disabled?: boolean;
  onPressAction?: string;
}

export interface CollapsibleNavItemComponent extends BaseComponent {
  componentType: ComponentType.COLLAPSIBLE_NAV_ITEM;
  name: string;
  label: string;
  icon?: string;
  tooltip?: string;
  isActive?: boolean;
  disabled?: boolean;
  defaultExpanded?: boolean;
  submenuItems: NavItemComponent[];
  submenuMode?: "inline" | "outside"; // Mode for how submenu opens
  submenuBackgroundColor?: string;
  submenuTextColor?: string;
}

export interface SummaryListField {
  IncludeLabel: boolean;
  Label: string;
  BindValue: string;
  Json_Attribute: string;
  ControlType?: string;
  componentType?: ComponentType;
  AutoPopulate?: boolean;
  AutoPopulate_format?: string;
  showInSummary: boolean;
  LookupName?: string;
  IsMandatory?: boolean;
  ChangeTextColor?: boolean;
  Text_class_fail?: string;
  Text_class_success?: string;
  TextColorCondition?: string;
  IsPrimary?: boolean;
  IsClear?: boolean;
  IncludeNotSet?: boolean;
  StickyColumn?: "left" | "right" | boolean; // Mark column as sticky. "left" or "right" for position, true defaults to "left"
  StickyWidth?: string; // Optional custom width for sticky column (e.g., "200px", "150px")
  valueFormat?: string; // Custom format for displaying values (e.g., "duration", "currency", "percentage")
}

export interface SummaryListActionConfig {
  Action: {
    Action: string;
    Navigate?: string;
    Module?: string;
    [key: string]: string | undefined;
  };
  ActionType: ActionType | string;
  ScreenId?: string;
  tooltip?: string; // Optional tooltip for the action button
  Label?: string;
  visibility?: number[];
  visibleForStatus?: string[];
  /** If set, this action button is hidden when the user lacks this permission for the screen's screenId */
  permissionKey?: PermissionKey;
}

export interface SummaryListFormConfig {
  Name: string;
  TableName?: string;
  View?: string;
  Filter?: string;
  DisplayName?: string;
  Query?: string;
  Fields: SummaryListField[];
  Workflow?: string | null;
  CheckBranch?: boolean;
}

export interface WorkflowItem {
  Name: string;
  Label: string;
  States?: string[];
  Action: string;
  Screen: string;
  EnableOn?: string[];
  ActionArgs?: Record<string, string>;
  Action_ref_column?: string;
}

export interface SummaryListComponent extends BaseComponent {
  componentType: ComponentType.SUMMARY_LIST;
  titleValue: string;
  apiname?: string;
  Name?: string;
  TableName?: string;
  View?: string;
  Filter?: string;
  DisplayName?: string;
  fields?: SummaryListField[];
  SummeryActions?: SummaryListActionConfig[];
  Form?: SummaryListFormConfig;
  workflow?: WorkflowItem[];
  FooterControls?: ComponentConfig[]; // Footer controls for totals/summary row
  BindValue?: string;
  EnableReport?: boolean;
  Plugged_json?: string;
  PluggingType?: string;
  HasLockedColumn?: boolean;
}

export interface WebViewComponent extends BaseComponent {
  componentType: ComponentType.WEB_VIEW;
  name?: string;
  title?: string;
  reportUrl?: string;
  reportName?: string;
}

export interface SubModuleComponent extends BaseComponent {
  componentType: ComponentType.SUB_MODULE;
  name: string;
  label?: string;
  tableName?: string;
  lookupName?: string; // Lookup name to fetch data from
  parentField?: string; // Parent field name that triggers data fetch
  childControls?: ComponentConfig[];
  FooterControls?: ComponentConfig[]; // Footer controls for totals/summary row
  validation?: ValidationConfig[];
  required?: boolean;
  disabled?: boolean;
  maxRows?: number;
  minRows?: number;
  enableAddAction?: boolean; // Enable/disable the add button (default: true)
  enableSearch?: boolean; // Enable/disable search functionality (default: false)
  addButtonPosition?: "top" | "bottom"; // Position of Add New Row button (default: "bottom")
  enableEditAction?: boolean; // Enable/disable the edit button (default: true, can be overridden by global preference)
  enableDeleteAction?: boolean; // Enable/disable the delete button (default: true, can be overridden by global preference)
  viewAction?: {
    // Configuration for view action (eye icon)
    pdfSettings?: string; // PDF report name (e.g., "invoice_pdf_view")
    reportApi?: string; // Alternative: API name for report
    drawerReportName?: string; // HTML report name - opens side drawer like Jobcard Summary (e.g., "invoice_summary_details")
    openPdfInNewTab?: boolean; // If true, open PDF in new browser tab instead of embedded viewer (avoids content policy / CSP errors)
    idField?: string; // Field name in row data that contains the ID (default: "id" or "invoice_id")
  };
  helperText?: string;
  errorText?: string;
  DisableAddNewRow?: boolean;
  NotAllowEmpty?: boolean;
}

export interface MultiSubListComponent extends BaseComponent {
  componentType: ComponentType.MULTI_SUB_LIST;
  name: string;
  label?: string;
  tableName?: string;
  lookupName?: string;
  childControls?: ComponentConfig[];
  validation?: ValidationConfig[];
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  errorText?: string;
}

export interface MultiCheckBoxComponent extends BaseComponent {
  componentType: ComponentType.MULTI_CHECKBOX;
  name: string;
  label?: string;
  options: DropdownOption[];
  lookupName?: string;
  defaultValue?: (string | number)[];
  validation?: ValidationConfig[];
  required?: boolean;
  disabled?: boolean;
  direction?: FlexDirection;
  maxSelections?: number;
  helperText?: string;
  errorText?: string;
}

export interface ReportComponent extends BaseComponent {
  componentType: ComponentType.REPORT;
  name?: string;
  label?: string;
  BindValue?: string;
  Settings?: string; // Report configuration name (e.g., "Vehicle_details")
  Parent_Field?: string; // Field name to get parent ID from formData
  IncludeLabel?: boolean;
  [key: string]: any; // Allow additional properties for flexibility
}

export interface FilesComponent extends BaseComponent {
  componentType: ComponentType.FILES;
  name: string;
  label?: string;
  placeholder?: string;
  validation?: ValidationConfig[];
  required?: boolean;
  disabled?: boolean;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  acceptedFileTypes?: string[];
  uploadAction?: string;
  previewEnabled?: boolean;
  helperText?: string;
  errorText?: string;
  Json_Attribute?: string;
  BindValue?: string;
  IsVerticalView?: boolean;
}

export interface CheckboxComponent extends BaseComponent {
  componentType: ComponentType.CHECKBOX;
  name: string;
  label?: string;
  defaultValue?: string | boolean;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  errorText?: string;
  Json_Attribute?: string;
  BindValue?: string;
}

export interface CustomComponent extends BaseComponent {
  componentType: ComponentType.CUSTOM;
  customType: string;
  props?: Record<string, any>;
  name?: string;
}

export interface InfoCardConfig {
  name: string;
  bindValue: string;
  icon: string;
  color?: string;
  defaultValue: number | string;
  valueFormat: string;
}

export interface InfoCardsComponent extends BaseComponent {
  componentType: ComponentType.INFO_CARDS;
  apiname: string;
  cards: InfoCardConfig[];
}

export interface CheckInOutComponent extends BaseComponent {
  componentType: ComponentType.CHECK_IN_OUT_COMPONENT;
  dataApi: string;
  onPressAction: string;
  logButtonAction?: string;
  visibility?: number[];
}

export interface LeaveBalanceType {
  id: string | number;
  total_days: number;
  remaining_days: number;
  leave_types?: {
    name: string;
  };
  [key: string]: any;
}

export interface LeaveBalanceComponent extends BaseComponent {
  componentType: ComponentType.LEAVE_BALANCE;
  title?: string;
  leaveBalances?: LeaveBalanceType[];
  apiname?: string;
}

export interface ProfileHeaderCardComponent extends BaseComponent {
  componentType: ComponentType.PROFILE_HEADER_CARD;
  fields?: ComponentConfig[];
}

export interface ProfileContactInfoComponent extends BaseComponent {
  componentType: ComponentType.PROFILE_CONTACT_INFO;
  fields?: ComponentConfig[];
}

export interface ProfileDepartmentInfoComponent extends BaseComponent {
  componentType: ComponentType.PROFILE_DEPARTMENT_INFO;
  fields?: ComponentConfig[];
}

export interface ProfileTabsConfig {
  tabs: string[];
  tabConfigs: Record<string, {
    visibility: number[];
    sections: {
      sectionId: string;
      title: string;
      editDrawerId?: string;
      editTitle?: string;
      editConfigId?: string;
      saveActionId?: string;
      fields: ComponentConfig[];
      enabledForCountries?: string[];
    }[];
  }>;
}

export interface ProfileTabsComponent extends BaseComponent {
  componentType: ComponentType.PROFILE_TABS;
  config: ProfileTabsConfig;
}


export type ComponentConfig =
  | ScreenGroupComponent
  | GroupComponent
  | ButtonComponent
  | InfoCardComponent
  | GridComponent
  | FieldGroupComponent
  | FormComponent
  | TextFieldComponent
  | TextAreaComponent
  | RadioFieldComponent
  | DateFieldComponent
  | DateTimeFieldComponent
  | SearchableDropdownComponent
  | SelectDropdownComponent
  | ImageUploadComponent
  | NavItemComponent
  | CollapsibleNavItemComponent
  | SummaryListComponent
  | WebViewComponent
  | SubModuleComponent
  | MultiSubListComponent
  | MultiCheckBoxComponent
  | ReportComponent
  | FilesComponent
  | CheckboxComponent
  | CustomComponent
  | InfoCardsComponent
  | LeaveBalanceComponent
  | LeaveBalanceComponent
  | CheckInOutComponent
  | ProfileHeaderCardComponent
  | ProfileContactInfoComponent
  | ProfileDepartmentInfoComponent
  | ProfileTabsComponent;
